/**
 * @file tool-coordinator.ts
 * @description Handles tool shortcuts, Eye Dropper, and Drawing Status Bar updates.
 */

import { g, layers, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { PickedColor, EyeDropperTool } from '../eyedropper';

const eyeDropperTool = new EyeDropperTool();

const drawingInfo = document.getElementById('drawingInfo');
const startXInput = document.getElementById('startXInput') as HTMLInputElement | null;
const startYInput = document.getElementById('startYInput') as HTMLInputElement | null;
const widthInput = document.getElementById('widthInput') as HTMLInputElement | null;
const heightInput = document.getElementById('heightInput') as HTMLInputElement | null;
const angleInput = document.getElementById('angleInput') as HTMLInputElement | null;
const radiusInput = document.getElementById('radiusInput') as HTMLInputElement | null;
const radiusContainer = document.getElementById('radiusContainer') as HTMLElement | null;
const toolNameEl = document.getElementById('toolName');
const mousePositionEl = document.getElementById('mousePosition');

let shiftPressed = false;

window.addEventListener('keydown', (e) => { if (e.key === 'Shift') shiftPressed = true; });
window.addEventListener('keyup', (e) => { if (e.key === 'Shift') shiftPressed = false; });

/**
 * Updates the drawing status bar with current coordinates and dimensions.
 */
export function updateDrawingStatusBar(e?: MouseEvent) {
    const toolId = (g.current_tool as any)?.id || '';
    const isVectorTool = toolId.match(/Line|Circle|Rectangle|Ellipse|Rounded_Rectangle/i);
    const selectionTools = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-single-row-select', 'btn-single-column-select', 'btn-lasso', 'btn-poly-select',
        'RectSelect', 'EllipseSelect', 'CircleSelect', 'Lasso', 'PolySelect'
    ];
    const isSelectionTool = selectionTools.some(id => toolId === id);
    const isSelectionGhost = isSelectionTool && (g as any).settings?.selection?.ghostMode && g.isSelectionActive;
    const vtm = (window as any).vectorToolManager;
    const isVectorEditing = !!vtm?.isEditing && !!vtm?.currentShape;
    const isVectorSelectTool = toolId === Tool.VectorSelect.id || toolId === 'btn-vector-select' || toolId.includes('VectorSelect');
    
    // Allow showing status bar only for geometric shape tools and selection tools
    const shouldShowBar = isVectorTool || isSelectionTool || isSelectionGhost || isVectorEditing || isVectorSelectTool;

    if (!g.drawing || !drawingInfo || !shouldShowBar) {
        // Only hide if NOT in edit mode (Ghost mode)
        if (!isVectorEditing && !isSelectionGhost) {
            const actions = document.querySelector('.drawing-actions') as HTMLElement;
            if (drawingInfo) {
                drawingInfo.style.display = shouldShowBar ? 'flex' : 'none';
                if (actions) actions.style.display = (g.drawing || isSelectionGhost) ? 'flex' : 'none';
            }
        }
        
        if (!isSelectionGhost && !isVectorEditing) return;
    }

    if (drawingInfo && shouldShowBar) drawingInfo.style.display = 'flex';
    
    let start = { x: g.startX, y: g.startY }; 
    let current = { x: g.pX, y: g.pY }; 

    // Vector editing: drive fields from current shape, not toolId-derived mouse deltas.
    if (isVectorEditing) {
        const s = vtm.currentShape as any;
        start = { x: s.x1, y: s.y1 };
        current = { x: s.x2, y: s.y2 };
    }

    const isDraggingHandle = (g as any)._draggingCornerIndex >= 0 || (g as any)._draggingPointIndex >= 0;

    // Feature BUG: If editing existing ghost selection OR dragging a handle, use shape bounds instead of raw mouse
    if ((isSelectionGhost || isDraggingHandle) && (g as any)._selectionEditBounds) {
        const b = (g as any)._selectionEditBounds;
        start = { x: b.x1, y: b.y1 };
        current = { x: b.x2, y: b.y2 };
    }

    let dx = current.x - start.x;
    let dy = current.y - start.y;
    
    // Feature BUG: Handle square snapping in status bar for CircleSelect to match actual mask
    const isCircle = toolId.includes('CircleSelect');
    if (isCircle && g.drawing && !isSelectionGhost) {
        const side = Math.max(Math.abs(dx), Math.abs(dy));
        dx = (dx < 0 ? -side : side);
        dy = (dy < 0 ? -side : side);
    }

    // Ensure we are showing current tool's info
    const tool = toolId;
    const isEllipse = tool.includes('Circle') || tool.includes('Ellipse');
    const isLine = tool.includes('Line');
    const isRect = tool.includes('Rectangle');

    if (radiusContainer && radiusInput) {
        const showRadius = isVectorEditing ? ((vtm.currentShape as any)?.type === 'circle') : isEllipse;
        if (showRadius) {
            radiusContainer.style.display = 'flex';
            radiusInput.value = Math.round(Math.hypot(dx, dy)).toString();
        } else {
            radiusContainer.style.display = 'none';
        }
    }

    // For vector editing, angle comes from geometry (line) or style.angle (others).
    if (isVectorEditing && angleInput) {
        const s = vtm.currentShape as any;
        if (s.type === 'line') {
            let a = Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 180 / Math.PI;
            if (a < 0) a += 360;
            angleInput.value = Math.round(a).toString();
        } else {
            angleInput.value = Math.round(s.style?.angle || 0).toString();
        }
    } else if (shiftPressed && isLine) {
        const angle = Math.atan2(dy, dx) * 180 / Math.PI;
        const allowedAngles = [0, 30, 45, 60, 90, 120, 135, 150, 180, -30, -45, -60, -90, -120, -135, -150, -180];
        const finalSnap = allowedAngles.reduce((prev, curr) => Math.abs(curr - angle) < Math.abs(prev - angle) ? curr : prev);
        if (angleInput) angleInput.value = Math.round(finalSnap).toString();
    } else if (angleInput) {
        let angle = Math.atan2(dy, dx) * 180 / Math.PI;
        if (angle < 0) angle += 360;
        angleInput.value = Math.round(angle).toString();
    }

    if (startXInput) startXInput.value = Math.round(start.x).toString();
    if (startYInput) startYInput.value = Math.round(start.y).toString();
    if (widthInput) widthInput.value = Math.round(Math.abs(dx)).toString();
    if (heightInput) heightInput.value = Math.round(Math.abs(dy)).toString();

    // Enable inputs even on raster layer IF drawing/editing is active
    const disableInputs = !isVectorTool && !isSelectionGhost && !isVectorEditing;
    [startXInput, startYInput, widthInput, heightInput, angleInput, radiusInput].forEach(el => {
        if (el) el.disabled = disableInputs;
    });
}

function updateBasicStatusBar() {
    if (toolNameEl) {
        const name = (g.current_tool as any)?.name || (g.current_tool as any)?.id || 'Tool';
        toolNameEl.textContent = name;
    }
    if (mousePositionEl) {
        mousePositionEl.textContent = `X: ${Math.round(g.pX || 0)}, Y: ${Math.round(g.pY || 0)}`;
    }
}

/**
 * Handle Eye Dropper color picking.
 */
export function handleEyeDropper(e: MouseEvent) {
    if (g.current_tool?.id !== Tool.Eye_Dropper.id) return;
    
    // Only pick if mouse is down (buttons === 1) or it's a click
    if (e.type === 'mousemove' && e.buttons !== 1) return;

    const canvas = document.getElementById("drawingCanvas") as HTMLCanvasElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const zoom = g.zoom || 1.0;
    
    // Convert viewport coordinates to internal canvas coordinates by accounting for zoom
    const x = Math.floor((e.clientX - rect.left) / zoom);
    const y = Math.floor((e.clientY - rect.top) / zoom);

    // Sanity check bounds
    if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) return;

    try {
        const visibleLayers = layers.filter(l => l.visible);
        const stack = visibleLayers.map(l => l.canvas as HTMLCanvasElement);
        
        const pickedColor: PickedColor = eyeDropperTool.pickColor(stack, x, y);
        const color = pickedColor.hex;
        
        // Update core state
        (g as any).pen_color = color;
        
        // Notify UI components
        window.dispatchEvent(new CustomEvent('colorChanged', { 
            detail: { color: color, source: 'eye-dropper' } 
        }));
        
        EventBus.emit('UPDATE_PROPERTIES_PANEL', { type: 'color', value: color });
        
        console.log('[EyeDropper] Picked color:', color, 'at', x, y);
    } catch (error) {
        console.error('[EyeDropper] Error picking color:', error);
    }
}

export function initToolCoordinator() {
    // Commit/Cancel drawing logic
    (window as any).commitDrawing = function() {
        const toolId = (g.current_tool as any)?.id;
        
        if ((window as any).vectorToolManager && (window as any).vectorToolManager.isEditing) {
            (window as any).vectorToolManager.commitEdit();
        } else if (g.isSelectionActive && (g as any).settings?.selection?.ghostMode) {
            // Feature BUG #37: Finalize selection ghost mode
            g.drawing = false;
            (window as any).polyPoints = [];
            (window as any).lassoPoints = [];
            (g as any)._selectionEditBounds = null;
            (g as any)._lassoEditPoints = null;
            (g as any)._selectionBaseCanvas = null;
            (g as any)._draggingPointIndex = -1;
            (g as any)._draggingCornerIndex = -1;
        } else {
            EventBus.emit('FINISH_DRAWING');
        }
        g.drawing = false;
        if (drawingInfo) drawingInfo.style.display = 'none';
        EventBus.emit('RENDER_LAYERS');
    };

    (window as any).cancelDrawing = function() {
        if ((window as any).vectorToolManager && (window as any).vectorToolManager.isEditing) {
            (window as any).vectorToolManager.cancelEdit();
        } else if (g.isSelectionActive) {
            // Feature BUG #37: Cancel selection ghost mode
            if (typeof (window as any).deselect === 'function') {
                (window as any).deselect();
            }
            g.drawing = false;
            (window as any).polyPoints = [];
            (window as any).lassoPoints = [];
            (g as any)._selectionEditBounds = null;
            (g as any)._lassoEditPoints = null;
            (g as any)._selectionBaseCanvas = null;
            (g as any)._draggingPointIndex = -1;
            (g as any)._draggingCornerIndex = -1;
        } else {
            g.drawing = false;
        }
        if (drawingInfo) drawingInfo.style.display = 'none';
        EventBus.emit('RENDER_LAYERS');
    };

    // Tool shortcuts and UI synchronization
    EventBus.on('TOOL_CHANGED', (data: { toolId: string }) => {
        const id = data.toolId;
        const selectionTools = [
            'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
            'btn-single-row-select', 'btn-single-column-select', 'btn-lasso', 'btn-poly-select',
            'RectSelect', 'EllipseSelect', 'CircleSelect', 'Lasso', 'PolySelect'
        ];
        const isSelectionTool = selectionTools.some(tid => id === tid);
        
        const vectorTools = [
            'btn-line', 'btn-circle', 'btn-rect', 'btn-ellipse', 'btn-rounded-rect',
            'Tool.Line.id', 'Tool.Circle.id', 'Tool.Rectangle.id', 'Tool.Ellipse.id'
        ];
        const isVectorTool = vectorTools.some(tid => id === tid);

        if (!(g as any).settings) (g as any).settings = {};
        if (!(g as any).settings.selection) (g as any).settings.selection = {};
        if (!(g as any).settings.vector) (g as any).settings.vector = {};

        // Feature BUG: Ghost Mode ON for selection tools by default.
        // IMPORTANT: Do NOT override vector.ghostMode here; it is a global persisted setting (Settings > Vector).
        if (isSelectionTool) {
            (g as any).settings.selection.ghostMode = true;
        }

        // Sync local settings checkbox if it exists
        const chkGhostMode = document.getElementById('chkGhostMode') as HTMLInputElement | null;
        if (chkGhostMode) {
            chkGhostMode.checked = !!(g as any).settings.selection.ghostMode;
        }
        
        console.log(`[ToolCoordinator] Tool changed to ${id}, SelectionGhost=${(g as any).settings.selection.ghostMode}`);

        // Keep status bar tool name in sync even if other UI listeners fail.
        updateBasicStatusBar();
    });

    EventBus.on('CANVAS_MOUSE_MOVE', (e: MouseEvent) => {
        updateDrawingStatusBar(e);
        handleEyeDropper(e);
        updateBasicStatusBar();
    });

    EventBus.on('DRAWING_STATUS_UPDATE', () => {
        updateDrawingStatusBar();
    });

    EventBus.on('CANVAS_MOUSE_DOWN', (e: MouseEvent) => {
        handleEyeDropper(e);
    });

    const updateFromInputs = () => {
        const vtm = (window as any).vectorToolManager;
        const toolId = (g.current_tool as any)?.id;
        const isVectorEditing = vtm && vtm.isEditing;
        const isSelectionGhost = g.isSelectionActive && (g as any).settings?.selection?.ghostMode;

        if (!isVectorEditing && !isSelectionGhost) return;

        const newX1 = parseFloat(startXInput?.value || '0');
        const newY1 = parseFloat(startYInput?.value || '0');
        const w = parseFloat(widthInput?.value || '0');
        const h = parseFloat(heightInput?.value || '0');
        const radius = parseFloat(radiusInput?.value || '0');
        const angle = parseFloat(angleInput?.value || '0');

        if (isVectorEditing && vtm.currentShape) {
            // Use the manager's central update method to ensure all events and overlay refreshes trigger correctly.
            vtm.updateShapeGeometry({}, {
                x1: newX1,
                y1: newY1,
                x2: newX1 + w,
                y2: newY1 + h,
                radius,
                angle
            });
            // Specifically for lines, x2/y2 are calculated from angle if provided in the manager, 
            // but for simple rects/ellipses we just send the bounding box.
        } else if (isSelectionGhost) {
            // Feature BUG #37: Update selection from status bar inputs, sync _selectionEditBounds
            const isCircle = toolId.includes('CircleSelect');
            let finalW = w;
            let finalH = h;
            
            if (isCircle) {
                // If radius was changed, use it to sync W and H
                const currentR = parseFloat(radiusInput?.value || '0');
                if (currentR * 2 !== w && currentR > 0) {
                    finalW = currentR * 2;
                    finalH = finalW;
                } else {
                    finalW = Math.max(w, h);
                    finalH = finalW;
                }
                
                if (widthInput) widthInput.value = Math.round(finalW).toString();
                if (heightInput) heightInput.value = Math.round(finalH).toString();
                if (radiusInput) radiusInput.value = Math.round(finalW / 2).toString();
            }

            const b = { x1: newX1, y1: newY1, x2: newX1 + finalW, y2: newY1 + finalH };
            (g as any)._selectionEditBounds = b;
            
            // Feature BUG #37: Update status bar with absolute document pixels for next frame
            (g as any).startX = b.x1;
            (g as any).startY = b.y1;
            (g as any).pX = b.x2;
            (g as any).pY = b.y2;

            const mode = ((g as any)._lastSelectionMode || 'replace') as any;
            const isRect = toolId.includes('RectSelect');
            const isEllipse = toolId.includes('EllipseSelect');

            if (isRect) {
                import('@hcie/tools').then(m => m.buildRectSelection(b.x1, b.y1, b.x2, b.y2, mode));
            } else if (isEllipse) {
                import('@hcie/tools').then(m => m.buildEllipseSelection(b.x1, b.y1, b.x2, b.y2, mode));
            } else if (isCircle) {
                import('@hcie/tools').then(m => m.buildCircleSelection(b.x1, b.y1, b.x2, b.y2, mode));
            }
            EventBus.emit('RENDER_LAYERS');
        }
    };

    [startXInput, startYInput, widthInput, heightInput, angleInput, radiusInput].forEach(el => {
        if (el) {
            el.addEventListener('input', updateFromInputs);
            el.addEventListener('change', updateFromInputs);
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    updateFromInputs();
                    (window as any).commitDrawing?.();
                }
                if (e.key === 'Escape') {
                    (window as any).cancelDrawing?.();
                }
            });
        }
    });
    
    const btnCommit = document.getElementById('btnCommitDrawing');
    const btnCancel = document.getElementById('btnCancelDrawing');
    if (btnCommit) btnCommit.addEventListener('click', (window as any).commitDrawing);
    if (btnCancel) btnCancel.addEventListener('click', (window as any).cancelDrawing);

    const chkGhostMode = document.getElementById('chkGhostMode') as HTMLInputElement | null;
    if (chkGhostMode) {
        // Sync initial state
        if ((g as any).settings?.selection) {
            chkGhostMode.checked = !!(g as any).settings.selection.ghostMode;
        }
        
        chkGhostMode.addEventListener('change', () => {
            if (!(g as any).settings) (g as any).settings = {};
            if (!(g as any).settings.selection) (g as any).settings.selection = {};
            (g as any).settings.selection.ghostMode = chkGhostMode.checked;
            console.log(`[GhostMode] Toggled to ${chkGhostMode.checked}`);
            
            // If turned OFF while active, commit immediately
            if (!chkGhostMode.checked) {
                (window as any).commitDrawing?.();
            }
        });
    }

    // Set initial defaults for the current tool
    if (!(g as any).settings) (g as any).settings = {};
    if (!(g as any).settings.selection) (g as any).settings.selection = {};
    if (!(g as any).settings.vector) (g as any).settings.vector = {};
    
    // Default: ON for selection. Vector ghost mode is persisted via SettingsManager.
    const selectionTools = ['btn-rect-select', 'btn-ellipse-select', 'btn-circle-select', 'btn-lasso', 'btn-poly-select'];
    const isSelection = selectionTools.includes(g.current_tool.id);
    (g as any).settings.selection.ghostMode = isSelection;
    // Leave (g as any).settings.vector.ghostMode untouched.
    
    if (chkGhostMode) chkGhostMode.checked = isSelection;
}
